import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FlightTakeoff } from '@mui/icons-material';
import { RootState } from '../../store/store';
import { plannerFingerprint } from '../../store/slices/missionPlannerSlice';
import { fleetRefreshRequested } from '../../store/slices/dronesSlice';
import { commandService } from '../../services/commandService';
import { droneLabel } from '../../types/drone';
import { ApiError } from '../../types/api';
import { HoldToConfirmButton } from '../Common/HoldToConfirmButton';

/**
 * Flying the saved route from the Planner (#632, F3 (a), F4 (a)).
 *
 * The pilot launches what they are looking at: the saved draft, assigned to a
 * drone, unchanged since it was saved. A confirm dialog shows the drone, the
 * route, the take-off height and what the route ends in. "Load route" sends
 * fly_route naming the draft and its revision, and the API builds the
 * waypoints from it (F6). Then holding the button arms and takes off; the
 * route starts at the take-off altitude. A loaded route whose take-off is
 * refused shows as loaded, on the ground. Landing, return to base and emergency
 * land stay in the drone panel on the map.
 */

/** What a route can end in (#632, I3, F1), in a pilot's words. */
const ENDS = {
  return_to_base: 'Return to where it took off, and land',
  land: 'Land at the last waypoint',
  hold: 'Hover at the last waypoint until you act',
} as const;
type RouteEnd = keyof typeof ENDS;

const DEFAULT_TAKEOFF_ALTITUDE_M = 5;

/** Why a refusal happened, said so the pilot knows what to do next. */
const describeRefusal = (e: unknown): string => {
  const api = e as ApiError;
  switch (api?.serverCode) {
    case 'REVISION_CONFLICT':
      return 'The draft was saved again elsewhere since you opened it. Reopen it, then launch.';
    case 'DRAFT_NOT_FOR_THIS_DRONE':
      return 'The saved draft is planned for another drone. Choose this drone, save, then launch.';
    case 'MISSION_DRAFT_NOT_FOUND':
      return 'The draft no longer exists, or is not yours.';
    case 'DRAFT_NOT_FLYABLE':
      return api.serverMessage ?? 'The saved route cannot be flown as saved.';
    default:
      return api?.serverMessage || api?.message || 'The command could not be sent.';
  }
};

type Step = 'confirm' | 'loaded' | 'commanded';

export const LaunchRoutePanel: React.FC = () => {
  const dispatch = useDispatch();
  const draft = useSelector((s: RootState) => s.missionPlanner.draft);
  const returnToHome = useSelector((s: RootState) => s.missionPlanner.returnToHome);
  const droneId = useSelector((s: RootState) => s.missionPlanner.droneId);
  const savedDraftId = useSelector((s: RootState) => s.missionPlanner.savedDraftId);
  const savedDraftName = useSelector((s: RootState) => s.missionPlanner.savedDraftName);
  const savedRevision = useSelector((s: RootState) => s.missionPlanner.savedRevision);
  const savedFingerprint = useSelector((s: RootState) => s.missionPlanner.savedFingerprint);
  const drone = useSelector((s: RootState) => (droneId ? s.drones.drones[droneId] : undefined));
  const unchanged = useMemo(
    () => savedFingerprint !== null && savedFingerprint === plannerFingerprint({ draft, returnToHome, droneId }),
    [savedFingerprint, draft, returnToHome, droneId]
  );
  const roles = useSelector((s: RootState) => s.user.roles);
  // Visibility only: the API authorises every command.
  const mayCommand = roles.includes('operator') || roles.includes('admin');

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('confirm');
  const [altitude, setAltitude] = useState(String(DEFAULT_TAKEOFF_ALTITUDE_M));
  const [end, setEnd] = useState<RouteEnd>('return_to_base');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [terrainUnchecked, setTerrainUnchecked] = useState<string | null>(null);

  if (!mayCommand) return null;

  // On the ground, ready to take off, as the server judges it: a route loaded
  // on a drone in the air starts at once, which this launch is not.
  const onTheGround = (drone?.availableCommands ?? []).includes('takeoff');
  const why =
    !droneId
      ? 'Plan this route for a drone first.'
      : savedDraftId === null || savedRevision === null
        ? 'Save the draft first: only a saved route is flown.'
        : !unchanged
          ? 'Save your changes first: only the saved route is flown.'
          : !onTheGround
            ? 'The drone is not on the ground ready to take off. A route is launched from the ground.'
            : null;

  const start = () => {
    setStep('confirm');
    setError(null);
    setTerrainUnchecked(null);
    // The draft's own return-to-home toggle is the default end.
    setEnd(returnToHome ? 'return_to_base' : 'hold');
    setOpen(true);
  };

  const loadRoute = async (acknowledgeUncheckedTerrain = false) => {
    if (!droneId || savedDraftId === null || savedRevision === null) return;
    setBusy(true);
    setError(null);
    try {
      await commandService.send(
        droneId,
        'fly_route',
        {
          mission_draft_id: savedDraftId,
          mission_draft_revision: savedRevision,
          end_action: end,
        },
        { acknowledgeUncheckedTerrain }
      );
      setTerrainUnchecked(null);
      setStep('loaded');
    } catch (e) {
      const api = e as ApiError;
      if (api?.serverCode === 'TERRAIN_UNCHECKED') {
        setTerrainUnchecked(
          'The ground under some of the route could not be checked against the 120 m limit.'
        );
      } else {
        setError(describeRefusal(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const takeOff = async () => {
    if (!droneId) return;
    setBusy(true);
    setError(null);
    try {
      await commandService.send(droneId, 'takeoff', { altitude_m: Number(altitude) });
      // "commanded", never "flying" (#556): the aircraft has not done it yet.
      setStep('commanded');
      dispatch(fleetRefreshRequested());
    } catch (e) {
      setError(`Route loaded, on the ground: the take-off was refused. ${describeRefusal(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const altitudeInvalid = !Number.isFinite(Number(altitude)) || Number(altitude) <= 0;

  return (
    <Box>
      <Button
        variant="contained"
        startIcon={<FlightTakeoff />}
        disabled={why !== null}
        onClick={start}
        fullWidth
      >
        Fly this route
      </Button>
      {why && (
        <Typography variant="caption" color="text.secondary" component="p">
          {why}
        </Typography>
      )}

      <Dialog open={open} onClose={() => !busy && setOpen(false)} fullWidth>
        <DialogTitle>Fly {savedDraftName ?? 'this route'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography>
              <strong>Drone:</strong> {drone ? droneLabel(drone) : droneId}
            </Typography>
            <Typography>
              <strong>Route:</strong> {draft.waypoints.length} waypoints at{' '}
              {draft.profile.altitudeM} m, saved revision {savedRevision}
            </Typography>
            <TextField
              label="Take-off height (m)"
              type="number"
              value={altitude}
              onChange={(e) => setAltitude(e.target.value)}
              disabled={step !== 'confirm'}
              error={altitudeInvalid}
              helperText="Metres above the launch point. The route starts once it is reached."
            />
            <TextField
              select
              label="At the last waypoint"
              value={end}
              onChange={(e) => setEnd(e.target.value as RouteEnd)}
              disabled={step !== 'confirm'}
            >
              {(Object.keys(ENDS) as RouteEnd[]).map((k) => (
                <MenuItem key={k} value={k}>
                  {ENDS[k]}
                </MenuItem>
              ))}
            </TextField>
            {terrainUnchecked && (
              <Alert severity="warning">
                {terrainUnchecked} Load it anyway only if you know the ground along the route.
              </Alert>
            )}
            {step === 'loaded' && (
              <Alert severity="info">
                Route loaded, on the ground. Make sure the area above the drone is clear, then
                press and hold to arm and take off. Closing leaves the route loaded: Take off in
                the drone's panel would fly it.
              </Alert>
            )}
            {step === 'commanded' && (
              <Alert severity="success">
                Take-off commanded. Watch the drone on the map; land, return to base and
                emergency land are in its panel.
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            {step === 'commanded' ? 'Close' : step === 'loaded' ? 'Close, route stays loaded' : 'Cancel'}
          </Button>
          {step === 'confirm' && terrainUnchecked && (
            <Button onClick={() => loadRoute(true)} disabled={busy} color="warning">
              Load anyway
            </Button>
          )}
          {step === 'confirm' && !terrainUnchecked && (
            <Button
              onClick={() => loadRoute()}
              disabled={busy || altitudeInvalid}
              variant="contained"
              startIcon={busy ? <CircularProgress size={16} /> : undefined}
            >
              Load route
            </Button>
          )}
          {step === 'loaded' && (
            <HoldToConfirmButton
              label="Hold to arm and take off"
              onConfirm={takeOff}
              disabled={busy}
            />
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
